import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import client, { resolveMedia } from "../lib/api";
import VideoPlayer from "../components/VideoPlayer";

/**
 * SaiyanVictoria Terminal embed player.
 * Designed to run inside an <iframe> on https://terminal.thesaiyanvictoria.com/
 *
 * URL params (all optional):
 *   embed=1           hide chrome (header/footer), pure player surface
 *   autoplay=1        attempt autoplay (forces muted unless muted=0)
 *   muted=0|1         override muted state
 *   loop=1            loop the playing video
 *   bg=transparent    let parent terminal background show through
 *   fx=off            disable scanline / grid / aura overlays
 *   theme=mono|aura   color treatment (default aura)
 *   next=1            on ended, advance to next related video
 *   cat=Action        list-mode filter by category
 *   type=short|long   list-mode filter by type
 *   play=ID           direct play a video id
 *
 * postMessage protocol (target: window.parent, origin "*"):
 *   Outgoing: { source: "saiyan-nexus", type: "ready"|"play"|"pause"|"time"|"ended"|"error"|"loaded", payload }
 *   Incoming: { type: "play"|"pause"|"toggle"|"seek"|"mute"|"volume"|"load"|"next"|"prev", payload }
 */
const NS = "saiyan-nexus";

/* Known SaiyanVictoria characters used for the reverse-bridge "open profile" pill.
 * When a video is playing inside the parent terminal iframe, we scan the title,
 * description and tags for these names; if a match is found, we offer a one-tap
 * link back to the character's profile in the parent terminal app. */
const CHARACTERS = [
  { id: "victoria_black_goddess", name: "Victoria Black Goddess", aliases: ["victoria black goddess", "vbg"] },
  { id: "victoria_black", name: "Victoria Black", aliases: ["victoria black"] },
  { id: "evil_victoria", name: "Evil Victoria", aliases: ["evil victoria"] },
  { id: "wargirl", name: "Wargirl", aliases: ["wargirl", "war girl"] },
  { id: "vanessa", name: "Vanessa", aliases: ["vanessa"] },
  { id: "veronica", name: "Veronica", aliases: ["veronica"] },
  { id: "harmony", name: "Harmony", aliases: ["harmony"] },
  { id: "binary", name: "Binary", aliases: ["binary"] },
];

function detectCharacter(video) {
  if (!video) return null;
  const hay = [video.title, video.description, ...(video.tags || [])]
    .filter(Boolean).join(" ").toLowerCase();
  // Sort by alias length so multi-word names ("Victoria Black Goddess") win over "Victoria"
  const ordered = [...CHARACTERS].sort(
    (a, b) => Math.max(...b.aliases.map((x) => x.length)) - Math.max(...a.aliases.map((x) => x.length))
  );
  for (const c of ordered) {
    if (c.aliases.some((a) => hay.includes(a))) return c;
  }
  return null;
}

export default function TerminalPlayer() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const playId = id || params.get("play");
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") === "1";
  const mutedParam = params.get("muted");
  const loop = params.get("loop") === "1";
  const bgTransparent = params.get("bg") === "transparent";
  const fxOff = params.get("fx") === "off";
  const theme = params.get("theme") === "mono" ? "mono" : "aura";
  const nextOnEnd = params.get("next") === "1";
  const filterCat = params.get("cat") || "";
  const filterType = params.get("type") || "";
  const filterQ = params.get("q") || "";

  const [video, setVideo] = useState(null);
  const [list, setList] = useState([]);
  const [error, setError] = useState(null);
  const controlsRef = useRef(null);

  // Make body transparent when bg=transparent so parent terminal canvas shines through
  useEffect(() => {
    if (!bgTransparent) return;
    const prev = { bg: document.body.style.background, html: document.documentElement.style.background };
    document.body.style.background = "transparent";
    document.documentElement.style.background = "transparent";
    return () => {
      document.body.style.background = prev.bg;
      document.documentElement.style.background = prev.html;
    };
  }, [bgTransparent]);

  // ---------- postMessage bridge (outgoing) ----------
  const post = (type, payload = {}) => {
    if (!window.parent || window.parent === window) return;
    try {
      window.parent.postMessage({ source: NS, type, payload }, "*");
    } catch (_) { /* noop */ }
  };

  // ---------- postMessage bridge (incoming commands from parent terminal) ----------
  useEffect(() => {
    const onMsg = (e) => {
      const data = e?.data;
      if (!data || typeof data !== "object" || data.target !== NS) return;
      const c = controlsRef.current;
      switch (data.type) {
        case "play": c?.play(); break;
        case "pause": c?.pause(); break;
        case "toggle": {
          const s = c?.getState();
          if (!s) break;
          s.paused ? c.play() : c.pause();
          break;
        }
        case "seek": c?.seek(Number(data.payload?.t)); break;
        case "mute": c?.setMuted(!!data.payload?.muted); break;
        case "volume": c?.setVolume(Number(data.payload?.value)); break;
        case "load": {
          const id = data.payload?.id;
          const np = new URLSearchParams(params);
          // Filters can be supplied alongside or instead of an id
          ["cat", "type", "q"].forEach((k) => {
            const v = data.payload?.[k];
            if (v === undefined) return;
            if (v === "" || v === null) np.delete(k);
            else np.set(k, String(v));
          });
          if (id) navigate(`/terminal/${id}?${np.toString()}`);
          else navigate(`/terminal?${np.toString()}`);
          break;
        }
        case "filter": {
          const np = new URLSearchParams(params);
          ["cat", "type", "q"].forEach((k) => {
            const v = data.payload?.[k];
            if (v === undefined) return;
            if (v === "" || v === null) np.delete(k);
            else np.set(k, String(v));
          });
          // Filter switches to list mode (drops any current play id)
          navigate(`/terminal?${np.toString()}`);
          break;
        }
        case "next":
        case "prev": {
          if (!list.length || !video) break;
          const i = list.findIndex((x) => x.id === video.id);
          const nextIdx = data.type === "next" ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
          navigate(`/terminal/${list[nextIdx].id}?${params.toString()}`);
          break;
        }
        case "exit": navigate("/"); break;
        default: break;
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [list, video, navigate, params]);

  // Announce ready once on mount so parent terminal knows the iframe is alive
  useEffect(() => {
    post("ready", { route: playId ? "play" : "list", id: playId || null, embed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data
  useEffect(() => {
    if (playId) {
      client.get(`/videos/${playId}`)
        .then((r) => {
          setVideo(r.data);
          post("loaded", { id: r.data.id, title: r.data.title, type: r.data.type, duration: r.data.duration });
        })
        .catch(() => {
          setError("Signal lost");
          post("error", { reason: "video-not-found", id: playId });
        });
    } else {
      const qp = new URLSearchParams();
      if (filterCat) qp.set("category", filterCat);
      if (filterType) qp.set("type", filterType);
      if (filterQ) qp.set("q", filterQ);
      const url = `/videos${qp.toString() ? "?" + qp.toString() : ""}`;
      client.get(url).then((r) => {
        setList(r.data);
        post("filtered", { count: r.data.length, cat: filterCat, type: filterType, q: filterQ });
      });
    }
  }, [playId, filterCat, filterType, filterQ]);

  // Also fetch a list for next/prev navigation when in play mode
  useEffect(() => {
    if (!playId) return;
    client.get("/videos").then((r) => setList(r.data)).catch(() => {});
  }, [playId]);

  const initialMuted = useMemo(() => {
    if (mutedParam === "0") return false;
    if (mutedParam === "1") return true;
    return autoplay; // default: muted iff autoplay
  }, [mutedParam, autoplay]);

  const onVideoEnded = () => {
    post("ended", { id: video?.id });
    if (nextOnEnd && list.length && video) {
      const i = list.findIndex((x) => x.id === video.id);
      if (i >= 0) {
        const nx = list[(i + 1) % list.length];
        navigate(`/terminal/${nx.id}?${params.toString()}`);
      }
    }
  };

  const accent = theme === "mono" ? "#A1A1A1" : "#FFB800";
  const accent2 = theme === "mono" ? "#FFFFFF" : "#FF6B00";
  const rootBg = bgTransparent ? "bg-transparent" : "bg-black";
  const detectedCharacter = useMemo(() => detectCharacter(video), [video]);

  return (
    <div
      className={`min-h-screen ${rootBg} relative overflow-hidden`}
      data-testid="terminal-player-page"
      data-embed={embed ? "1" : "0"}
      data-theme={theme}
    >
      {!fxOff && (
        <>
          <div className="absolute inset-0 terminal-grid opacity-30" />
          <div className="scan-line" />
          {theme === "aura" && <div className="absolute inset-0 aura-glow pointer-events-none" />}
        </>
      )}

      {!embed && (
        <header
          className="relative z-10 px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: `${accent2}33` }}
        >
          <div
            className="font-mono text-xs uppercase tracking-[0.3em] terminal-flicker"
            style={{ color: accent }}
          >
            $ saiyan_terminal · nexus.player &gt;{" "}
            <span className="text-white">{playId ? `play/${playId.slice(0, 8)}` : "list"}</span>
          </div>
          <Link
            to="/"
            className="font-mono text-xs text-zinc-500 hover:text-white"
            data-testid="terminal-exit"
          >
            [ exit ]
          </Link>
        </header>
      )}

      <main className={`relative z-10 ${embed ? "p-0" : "px-6 py-8"}`}>
        {error && (
          <p className="text-red-400 font-mono text-sm" data-testid="terminal-error">
            {error}
          </p>
        )}

        {playId && video && (
          <div className={embed ? "w-full relative" : "max-w-5xl mx-auto"} data-testid="terminal-playing">
            {!embed && (
              <div className="font-mono text-xs mb-3" style={{ color: accent }}>
                ▶ playing: <span className="text-white">{video.title}</span>
              </div>
            )}
            <VideoPlayer
              src={resolveMedia(video.playback_url)}
              poster={resolveMedia(video.thumbnail)}
              vertical={video.type === "short"}
              autoPlay={autoplay}
              loop={loop}
              videoId={video.id}
              controlsRef={controlsRef}
              onEnded={onVideoEnded}
              onPlayEvt={() => post("play", { id: video.id })}
              onPauseEvt={() => post("pause", { id: video.id })}
              onTimeEvt={(t) => post("time", { id: video.id, ...t })}
              onErrorEvt={() => post("error", { id: video.id, reason: "playback" })}
            />
            {/* Reverse-bridge: if embedded inside the SaiyanVictoria Terminal AND
                the video mentions a known character, surface a one-tap link back
                to that character's profile via postMessage. */}
            {embed && detectedCharacter && (
              <button
                data-testid="open-character-profile"
                onClick={() => post("navigate", { route: `/characters/${detectedCharacter.id}`, character: detectedCharacter.id })}
                className="absolute top-3 left-3 z-20 px-3 h-8 leading-8 text-[10px] uppercase tracking-widest font-bold rounded-full bg-black/70 backdrop-blur border border-[#FF6B00]/60 text-[#FFB800] hover:text-white hover:border-[#FF6B00] transition-colors shadow-lg"
              >
                ↩ Open {detectedCharacter.name}'s Profile
              </button>
            )}
            {!embed && (
              <div className="mt-4 font-mono text-xs text-zinc-400">
                <p>
                  <span style={{ color: accent }}>category:</span> {video.category}
                </p>
                {video.tags?.length > 0 && (
                  <p>
                    <span style={{ color: accent }}>tags:</span> {video.tags.join(", ")}
                  </p>
                )}
                {video.description && (
                  <p className="mt-2 text-zinc-500 leading-relaxed">{video.description}</p>
                )}
              </div>
            )}
          </div>
        )}

        {!playId && (
          <div className={embed ? "w-full px-3 py-3" : "max-w-5xl mx-auto"} data-testid="terminal-list">
            <div className="font-mono text-xs mb-4" style={{ color: accent }}>
              ▶ ls /nexus/videos{filterCat ? ` --cat=${filterCat}` : ""}
              {filterType ? ` --type=${filterType}` : ""}
              {filterQ ? ` --q="${filterQ}"` : ""}
            </div>
            {list.length === 0 && (
              <p className="font-mono text-zinc-500 text-sm">// no transmissions yet</p>
            )}
            <ul className="font-mono text-sm divide-y divide-white/5 border border-white/5 rounded-lg overflow-hidden">
              {list.map((v, i) => (
                <li key={v.id} className="hover:bg-[#FF6B00]/5 transition-colors">
                  <button
                    data-testid={`terminal-row-${v.id}`}
                    onClick={() => {
                      post("select", { id: v.id, title: v.title });
                      const qs = params.toString();
                      navigate(`/terminal/${v.id}${qs ? `?${qs}` : ""}`);
                    }}
                    className="w-full text-left px-4 py-3 flex items-center gap-4"
                  >
                    <span className="text-zinc-600 w-8">
                      {String(i + 1).padStart(3, "0")}
                    </span>
                    <span
                      className="uppercase text-[10px] tracking-widest w-16"
                      style={{ color: accent }}
                    >
                      {v.type}
                    </span>
                    <span className="text-white flex-1 truncate">{v.title}</span>
                    <span className="text-zinc-500 text-xs hidden sm:inline">{v.category}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}
